import React from 'react';
import { observer } from 'mobx-react-lite';
import { ElevatedButton } from '@producer/ui-kit';
import styled from 'styled-components';

import { TaskCollection } from './task-collection';
import { SaveTaskDto, Task, TaskLevel } from '../../../app/entities';
import { ToolBar } from '../../../components/toolbar/toolbar';
import { GroupingDropdown } from './grouping-dropdown';
import { SpaceTasksFilters } from './space-tasks-filters/space-tasks-filters';
import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { ProjectTasksFilters } from './project-tasks-filters/project-tasks-filters';
import { Group, useTaskGroups } from '../task-grouping-options';

type TaskListProps = {
  tasks: Task[];
  taskLevel: TaskLevel;
  onClearFilters?: () => void;

  /**
   * Hide the toolbar (filters + grouping). Used when an outer view (e.g.
   * `TasksView`) already renders the toolbar.
   */
  hideToolbar?: boolean;

  /**
   * Override the default navigation when clicking on a task
   * @param taskId
   */
  onSelectTask?: (taskId: string) => void;

  /**
   * Default values to be used when creating task in this list
   *
   * It will be combined with the grouping defaultValues, the grouping default values
   * will have higher priority
   */
  defaultValues?: Partial<SaveTaskDto>;
};

export const TaskList: React.FC<TaskListProps> = observer(
  ({ tasks, taskLevel, onClearFilters, onSelectTask, defaultValues, hideToolbar }) => {
    const { activeGroupBy, groups } = useTaskGroups(tasks, taskLevel);

    return (
      <>
        {!hideToolbar && (
          <ToolBar>
            {taskLevel === 'space' ? <SpaceTasksFilters /> : <ProjectTasksFilters />}

            <div />

            <GroupingDropdown taskLevel={taskLevel} />
          </ToolBar>
        )}

        <Container>
          {tasks?.length ? (
            groups &&
            Object.values<Group>(groups)
              .sort(activeGroupBy.sortBy)
              .map((group: any) =>
                group.tasks.length ? (
                  <TaskCollection
                    key={`${group.value}-${group.name}-${group.tasks.length}`}
                    title={group.name}
                    value={group.value}
                    tasks={group.tasks}
                    getIcon={activeGroupBy.getIcon}
                    defaultValues={{ ...defaultValues, ...activeGroupBy.getDefaultValue(group.value) }}
                    taskLevel={taskLevel}
                    onSelectTask={onSelectTask}
                  />
                ) : null,
              )
          ) : (
            <PagePlaceholder
              title="No tasks matching the filters"
              actions={[
                <ElevatedButton
                  key="clear-filters"
                  text="Clear filters"
                  onClick={onClearFilters}
                />,
              ]}
              backgroundImgUrl="/images/order-from-chaos.svg"
              fullWidth
            />
          )}
        </Container>
      </>
    );
  },
);

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  overflow-x: clip;
  container-type: inline-size;
`;
